if (!customElements.get('product-details-table')) {
	customElements.define(
		'product-details-table',
		class ProductDetailsTable extends HTMLElement {
			constructor() {
				super();
				
				// State
				this.specsData = null;
				this.selectedVariant = null;
				this.variantChangeUnsubscriber = null;
				
				// DOM elements
				this.tableBody = null;
				this.variantMessage = null;
				this.scrollToFormLink = null;
			}

			connectedCallback() {
				this.initializeData();
				this.setupEventListeners();
				this.render();
			}

			disconnectedCallback() {
				if (this.variantChangeUnsubscriber) {
					this.variantChangeUnsubscriber();
				}
			}

			initializeData() {
				// Get specs data from JSON script
				const jsonElement = document.getElementById('product-details-json');
				if (jsonElement) {
					try {
						this.specsData = JSON.parse(jsonElement.innerHTML);
					} catch (error) {
						console.error('Error parsing product details JSON:', error);
					}
				}
			}

			setupEventListeners() {
				// Subscribe to variant change events
				if (window.PubSub && window.PUB_SUB_EVENTS) {
					this.variantChangeUnsubscriber = window.PubSub.subscribe(
						window.PUB_SUB_EVENTS.variantChange,
						this.handleVariantChange.bind(this)
					);
				}
			}

			handleVariantChange({ data: { variant } }) {
				this.selectedVariant = variant ? { ...variant } : null;
				this.render();
			}

			get hasMultipleVariants() {
				if (!this.specsData) return false;
				return Object.keys(this.specsData).length > 2;
			}

			get specSet() {
				if (!this.specsData) return {};
				
				if (!this.selectedVariant || !this.specsData[this.selectedVariant.id]) {
					return this.specsData.default || {};
				}
				
				return this.specsData[this.selectedVariant.id] || {};
			}

			scrollToForm() {
				const productInfo = document.querySelector('product-info');
				if (productInfo) {
					productInfo.scrollIntoView({ behavior: 'smooth' });
				}
			}

			render() {
				// Create the main container
				this.innerHTML = this.getTemplate();
				
				// Get references to DOM elements
				this.tableBody = this.querySelector('tbody');
				this.variantMessage = this.querySelector('.variant-message');
				this.scrollToFormLink = this.querySelector('.scroll-to-form');
				
				// Set up scroll to form functionality
				if (this.scrollToFormLink) {
					this.scrollToFormLink.addEventListener('click', (e) => {
						e.preventDefault();
						this.scrollToForm();
					});
				}
				
				// Update the table content
				this.updateTableContent();
			}

			getTemplate() {
				const hasMultipleVariants = this.hasMultipleVariants;
				const selectedVariant = this.selectedVariant;
				
				let messageHtml = '';
				
				if (selectedVariant && hasMultipleVariants) {
					messageHtml = `
						<p class="mb-6 text-dark-600">
							Showing specifications for the <strong>${selectedVariant.public_title || selectedVariant.title} option</strong>. 
							To view details for a different option, 
							<a href="#" class="scroll-to-form text-primary-900 hover:text-primary-800 hover:underline whitespace-nowrap">select it above</a>.
						</p>
					`;
				} else if (hasMultipleVariants) {
					messageHtml = `
						<p class="mb-6 text-dark-600">
							Showing general product specifications. To view option-specific details,  
							<a href="#" class="scroll-to-form text-primary-900 hover:text-primary-800 hover:underline whitespace-nowrap">select product options above</a>.
						</p>
					`;
				} else {
					messageHtml = '<div class="pb-3"></div>';
				}
				
				return `
					<div>
						${messageHtml}
						<table class="-mx-3 w-full">
							<tbody class="variant-message">
								<!-- Table rows will be populated here -->
							</tbody>
						</table>
					</div>
				`;
			}

			updateTableContent() {
				if (!this.tableBody) return;
				
				const specSet = this.specSet;
				const rows = Object.entries(specSet).map(([label, value]) => {
					return `
						<tr class="group odd:bg-dark-50">
							<th scope="row" class="text-start px-3 py-2">${label}</th>
							<td class="text-start px-3 py-2">${value}</td>
						</tr>
					`;
				}).join('');
				
				this.tableBody.innerHTML = rows;
			}

			escapeHtml(text) {
				if (typeof text !== 'string') return text;
				
				const div = document.createElement('div');
				div.textContent = text;
				return div.innerHTML;
			}

			// Public method to update data from sections API
			updateFromSectionsAPI(sectionData) {
				if (sectionData && sectionData.specsData) {
					this.specsData = sectionData.specsData;
					this.render();
				}
			}

			// Public method to manually set variant
			setVariant(variant) {
				this.selectedVariant = variant ? { ...variant } : null;
				this.render();
			}

			// Public method to refresh data
			refresh() {
				this.initializeData();
				this.render();
			}
		}
	);
}
